import type { QueryClient } from '@tanstack/react-query';
import { clearGuestXPFromSession } from '@/lib/gameSessionStorage';
import { GUEST_IDENTITY_UNAVAILABLE_CODE } from './guestCredential';
import {
  getAnonymousId,
  isParentManagedAnonymousIdentity,
  rotateAnonymousId,
} from './anonymousIdentity';
import { reportError, trackBreadcrumb } from '@/lib/telemetry';
import { isAnonymousIdentityLockConflict } from './selfIdentityWriteSerialization';
import {
  blockActiveRunPersistenceWrites,
  hasActiveRunPersistenceWrites,
  waitForActiveRunPersistenceWrites,
} from './persistenceWriteBarrier';
import {
  getActiveRunIdentityState,
  waitForAuthenticatedActiveRunIdentity,
  type AnonymousClaimOutcome,
  type EmbeddedGameRunIdentityContext,
} from './activeRunIdentity';

type PendingClaim = {
  promise: Promise<AnonymousClaimOutcome>;
  queryClients: Set<QueryClient>;
  startGuards: Set<() => boolean>;
  expectedAuthenticatedUserId: string;
};

type ClaimOptions = {
  /** Prevent a stale auth transition from starting the irreversible database claim. */
  canStart?: () => boolean;
  /** Binds the claim to the authenticated account that initiated the transition. */
  expectedAuthenticatedUserId: string;
};

const pendingClaims = new Map<string, PendingClaim>();
const claimRetryDelaysMs = [100, 200, 400, 800, 1600, 2000] as const;

/**
 * Resolves the guest principal eligible for an ambient account claim.
 * A mounted run owns its retained anonymous identity through every transition
 * state; browser storage is only the non-game fallback.
 */
export function resolveAnonymousClaimSource(): string | null {
  const activeRunIdentity = getActiveRunIdentityState().identity;
  if (activeRunIdentity?.isAnonymous) return activeRunIdentity.userId;
  return isParentManagedAnonymousIdentity() ? null : getAnonymousId();
}

const invalidateClaimedIdentity = async (queryClients: Set<QueryClient>): Promise<void> => {
  const invalidations: Array<Promise<unknown>> = [];
  for (const queryClient of queryClients) {
    for (const queryKey of [
      'dailyChallengeQuestions',
      'dailyChallengeStatus',
      'userStats',
      'userBadges',
      'dailyChallengeCompletion',
      'quizCompletion',
      'partnerStreakStatus',
    ]) {
      invalidations.push(queryClient.invalidateQueries({ queryKey: [queryKey] }));
    }
  }
  await Promise.allSettled(invalidations);
};

type CapturedAuthSession = {
  accessToken: string;
  userId: string;
};

const captureCurrentAuthSession = async (
  supabase: (typeof import('../supabase'))['supabase'],
  expectedUserId: string
): Promise<CapturedAuthSession | null> => {
  const auth = supabase.auth;
  if (!auth || typeof auth.getSession !== 'function') return null;
  const {
    data: { session },
    error,
  } = await auth.getSession();
  if (error || !session?.user || !session.access_token?.trim()) return null;
  if (session.user.id !== expectedUserId) return null;
  return { accessToken: session.access_token, userId: session.user.id };
};

/**
 * Claims one anonymous identity for the current authenticated session.
 * Calls are coalesced so the auth effect and the active-run coordinator cannot
 * race two transfers for the same browser identity.
 */
export async function claimAnonymousUserData(
  anonymousId: string,
  queryClient: QueryClient | undefined,
  options: ClaimOptions
): Promise<AnonymousClaimOutcome> {
  // Claiming is an identity transition even when this helper is invoked
  // directly by AnonymousClaimer. Close the dispatch gate before coalescing so
  // a queued guest write cannot slip in while the claim waits for its lease.
  blockActiveRunPersistenceWrites();

  const existingClaim = pendingClaims.get(anonymousId);
  if (existingClaim) {
    if (options.expectedAuthenticatedUserId !== existingClaim.expectedAuthenticatedUserId) {
      return 'failed';
    }
    if (queryClient) existingClaim.queryClients.add(queryClient);
    if (options.canStart) existingClaim.startGuards.add(options.canStart);
    return existingClaim.promise;
  }

  const queryClients = new Set<QueryClient>();
  const startGuards = new Set<() => boolean>();
  if (queryClient) queryClients.add(queryClient);
  if (options.canStart) startGuards.add(options.canStart);

  const claim = (async () => {
    let failureStage: 'session_capture' | 'database_claim' | 'local_identity_rotation' =
      'session_capture';
    try {
      if (hasActiveRunPersistenceWrites()) {
        await waitForActiveRunPersistenceWrites();
      }

      if ([...startGuards].some((guard) => !guard())) return 'failed';
      const { supabase } = await import('../supabase');
      if ([...startGuards].some((guard) => !guard())) return 'failed';

      for (let attempt = 0; attempt <= claimRetryDelaysMs.length; attempt++) {
        if ([...startGuards].some((guard) => !guard())) return 'failed';

        // Revalidate the initiating account before every dispatch and pin its
        // current token. A retry must never transfer this guest to another user.
        failureStage = 'session_capture';
        const authenticatedSession = await captureCurrentAuthSession(
          supabase,
          options.expectedAuthenticatedUserId
        );
        if (!authenticatedSession || [...startGuards].some((guard) => !guard())) return 'failed';

        failureStage = 'database_claim';
        const { error: rpcError } = await supabase
          .rpc('claim_anonymous_data', {
            p_anonymous_id: anonymousId,
          })
          .setHeader('Authorization', `Bearer ${authenticatedSession.accessToken}`);

        if (!rpcError) break;

        const retryDelay = claimRetryDelaysMs[attempt];
        if (isAnonymousIdentityLockConflict(rpcError) && retryDelay !== undefined) {
          await new Promise<void>((resolve) => setTimeout(resolve, retryDelay));
          continue;
        }
        const unavailableGuest = rpcError.code === GUEST_IDENTITY_UNAVAILABLE_CODE;
        if (unavailableGuest) {
          // This is a reversible no-transfer outcome, unlike an acknowledged
          // claim. Do not retire the source for a replaced initiating account.
          if (
            !(await captureCurrentAuthSession(supabase, options.expectedAuthenticatedUserId)) ||
            [...startGuards].some((guard) => !guard())
          )
            return 'failed';
        }
        if (
          unavailableGuest ||
          (rpcError.code === '42501' && rpcError.message === 'anonymous_identity_terminal')
        ) {
          await rotateAnonymousId(anonymousId);
          clearGuestXPFromSession();
          trackBreadcrumb('auth', 'anonymous_identity_claim_settled', { action: 'terminal' });
          return 'terminal';
        }
        console.error('Failed to claim anonymous data:', rpcError);
        reportError({
          error: new Error('Anonymous identity claim failed'),
          source: 'AnonymousIdentityClaim.settlement',
          context: { authStage: 'database_claim' },
          supabaseError: rpcError,
        });
        return 'failed';
      }

      if (import.meta.env.DEV) {
        console.log('Successfully claimed anonymous data and merged stats');
      }

      // A committed database claim is irreversible. Finish the exact local
      // identity transition even if the initiating UI closes or auth changes.
      failureStage = 'local_identity_rotation';
      await rotateAnonymousId(anonymousId);
      clearGuestXPFromSession();
      trackBreadcrumb('auth', 'anonymous_identity_claim_settled', { action: 'claimed' });

      // Every coalesced caller's QueryClient must observe the merged data;
      // invalidation cannot depend on whichever caller started first.
      await invalidateClaimedIdentity(queryClients);
      return 'claimed';
    } catch (err) {
      console.error('Failed to claim anonymous data:', err);
      reportError({
        error: err,
        source: 'AnonymousIdentityClaim.settlement',
        context: { authStage: failureStage },
      });
      return 'failed';
    }
  })();

  pendingClaims.set(anonymousId, {
    promise: claim,
    queryClients,
    startGuards,
    expectedAuthenticatedUserId: options.expectedAuthenticatedUserId,
  });
  void claim.finally(() => pendingClaims.delete(anonymousId));
  return claim;
}

/** Keep logout behind every identity transfer that started while this session was active. */
export async function waitForPendingAnonymousClaims(): Promise<void> {
  while (pendingClaims.size > 0) {
    await Promise.all([...pendingClaims.values()].map(({ promise }) => promise));
  }
}

export type AnonymousIdentitySettlement = AnonymousClaimOutcome | 'none';

/**
 * Settles the exact embedded guest captured before authentication and waits
 * for the mounted Game Run to become writable as that authenticated account.
 */
export async function settleEmbeddedAnonymousIdentity(
  sourceIdentity: EmbeddedGameRunIdentityContext,
  authenticatedUserId: string,
  queryClient?: QueryClient,
  options?: Pick<ClaimOptions, 'canStart'>
): Promise<AnonymousClaimOutcome> {
  const outcome = await claimAnonymousUserData(sourceIdentity.anonymousId, queryClient, {
    ...options,
    expectedAuthenticatedUserId: authenticatedUserId,
  });
  if (outcome === 'failed') return outcome;

  const rebound = await waitForAuthenticatedActiveRunIdentity(
    authenticatedUserId,
    sourceIdentity.generation
  );
  if (!rebound) {
    reportError({
      error: new Error('Authenticated Game Run rebound did not settle'),
      source: 'AnonymousIdentityClaim.settlement',
      context: { authStage: 'active_run_rebound' },
    });
  }
  return rebound ? outcome : 'failed';
}

/**
 * Finishes the current guest-to-account transition before sign-in UI reports
 * success. The underlying claim remains coalesced with the auth and Game Run
 * coordinators.
 */
export async function settleCurrentAnonymousIdentity(
  queryClient: QueryClient | undefined,
  authenticatedUserId: string
): Promise<AnonymousIdentitySettlement> {
  const anonymousId = resolveAnonymousClaimSource();
  if (anonymousId) {
    return claimAnonymousUserData(anonymousId, queryClient, {
      expectedAuthenticatedUserId: authenticatedUserId,
    });
  }
  await waitForPendingAnonymousClaims();
  return 'none';
}
